import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import mutate from "@/Utils/request/mutate";
import query from "@/Utils/request/query";
import { Permission } from "@/types/emr/permission/permission";
import permissionApi from "@/types/emr/permission/permissionApi";
import { RoleRead } from "@/types/emr/role/role";
import roleApi from "@/types/emr/role/roleApi";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

interface RoleFormProps {
  role: RoleRead | null;
  onSuccess: () => void;
}
const PAGE_LIMIT = 200;

type PermissionViewMode = "all" | "selected" | "unselected";

const SETTINGS_PERMISSION_SLUGS = new Set([
  "can_view_facility_general_settings",
  "can_update_facility",
  "can_create_facility_organization",
  "can_manage_facility_organization",
  "can_manage_facility_organization_users",
  "can_write_facility_locations",
  "can_manage_devices",
  "can_write_specimen_definition",
  "can_write_observation_definition",
  "can_write_activity_definition",
  "can_set_charge_item_definition",
  "can_write_charge_item_definition",
  "can_write_healthcare_service",
  "can_write_product_knowledge",
  "can_write_product",
  "can_write_token_category",
  "can_write_patient_identifier_config",
  "can_write_tag_config",
  "can_write_template",
  "can_manage_template",
]);

const PERMISSION_GROUPS = [
  {
    key: "settings",
    label: "Settings",
    matches: (slug: string) => SETTINGS_PERMISSION_SLUGS.has(slug),
  },
  {
    key: "encounter",
    label: "Encounter",
    matches: (slug: string) => slug.includes("encounter"),
  },
  {
    key: "patient",
    label: "Patient",
    matches: (slug: string) => slug.includes("patient"),
  },
  {
    key: "appointment",
    label: "Appointments",
    matches: (slug: string) =>
      slug.includes("booking") || slug.includes("schedule"),
  },
  {
    key: "billing",
    label: "Billing",
    matches: (slug: string) =>
      slug.includes("account") ||
      slug.includes("invoice") ||
      slug.includes("payment") ||
      slug.includes("charge_item"),
  },
  {
    key: "facility",
    label: "Facility",
    matches: (slug: string) =>
      slug.includes("facility") ||
      slug.includes("location") ||
      slug.includes("device"),
  },
  {
    key: "services",
    label: "Services",
    matches: (slug: string) =>
      slug.includes("healthcare_service") ||
      slug.includes("service_request") ||
      slug.includes("diagnostic_report"),
  },
  {
    key: "inventory",
    label: "Inventory",
    matches: (slug: string) =>
      slug.includes("inventory") ||
      slug.includes("product") ||
      slug.includes("supply") ||
      slug.includes("medication_dispense"),
  },
  {
    key: "questionnaire",
    label: "Questionnaire",
    matches: (slug: string) => slug.includes("questionnaire"),
  },
] as const;

const ENCOUNTER_CLINICAL_READ_DEPENDENCIES = [
  "can_list_encounter",
  "can_read_encounter",
];

const PERMISSION_DEPENDENCIES: Record<string, string[]> = {
  can_create_encounter: [
    "can_list_encounter",
    "can_read_encounter",
    "can_view_facility_organization",
    "can_list_facility_organization_users",
  ],
  can_write_encounter: ["can_list_encounter", "can_read_encounter"],
  can_read_encounter_clinical_data: ENCOUNTER_CLINICAL_READ_DEPENDENCIES,
  can_write_encounter_clinical_data: [
    "can_list_encounter",
    "can_read_encounter",
    "can_read_encounter_clinical_data",
  ],
  can_submit_encounter_questionnaire: [
    "can_list_encounter",
    "can_read_encounter",
    "can_read_encounter_clinical_data",
    "can_read_questionnaire",
    "can_submit_questionnaire",
  ],
  can_create_charge_item: ["can_read_charge_item"],
  can_write_service_request: ["can_read_service_request"],
  can_write_diagnostic_report: ["can_read_diagnostic_report"],
  can_write_specimen: ["can_read_specimen"],
  can_write_invoice: [
    "can_read_invoice",
    "can_read_account",
    "can_read_charge_item",
  ],
  write_medication_dispense: ["read_medication_dispense"],
  can_generate_report_from_template: ["can_read_template"],
  can_manage_device_associations_to_encounters: ["can_list_devices"],
  can_write_booking: [
    "can_list_booking",
    "can_view_facility_organization",
    "can_list_facility_organization_users",
  ],
  can_reschedule_booking: ["can_list_booking", "can_write_booking"],
  can_access_practitioner_workspace: ["can_list_booking", "can_write_booking"],
  can_access_nursing_actions: [
    "can_access_practitioner_workspace",
    "can_create_encounter",
    "can_create_patient",
    "can_write_booking",
    "can_write_patient",
  ],
  can_access_care_team_encounters: [
    "can_list_encounter",
    "can_read_encounter",
    "can_read_encounter_clinical_data",
  ],
  can_write_payment_reconciliation: [
    "can_read_payment_reconciliation",
    "can_read_invoice",
    "can_write_invoice",
    "can_read_account",
    "can_update_account",
  ],
  can_update_account: ["can_read_account"],
  can_create_account: ["can_read_account"],
  can_update_facility: ["can_view_facility_general_settings"],
  can_write_patient_identifier_config: ["can_read_patient_identifier_config"],
  can_write_tag_config: ["can_read_tag_config"],
  can_write_template: ["can_read_template"],
  can_write_specimen_definition: ["can_read_specimen_definition"],
  can_write_observation_definition: ["can_read_observation_definition"],
  can_write_activity_definition: ["can_read_activity_definition"],
  can_write_charge_item_definition: ["can_read_charge_item_definition"],
  can_write_healthcare_service: ["can_read_healthcare_service"],
  can_write_product_knowledge: ["can_read_product_knowledge"],
  can_write_product: ["can_read_product"],
  can_list_patients: [
    "can_read_patient_identifier_config",
    "can_read_tag_config",
  ],
  can_write_patient: ["can_list_patients"],
  can_create_patient: ["can_list_patients"],
  can_view_clinical_data: ["can_list_patients"],
  can_submit_patient_questionnaire: ["can_list_patients"],
  can_view_questionnaire_responses: ["can_list_patients"],
};

const PERMISSION_PARENT_LABELS: Record<string, string> = {
  can_list_encounter: "Encounter / Read access",
  can_read_encounter: "Encounter / Read access",
  can_read_encounter_clinical_data: "Encounter / Clinical read",
  can_create_encounter: "Encounter / Create",
  can_write_encounter: "Encounter / Non-clinical edit",
  can_write_encounter_clinical_data: "Encounter / Clinical edit",
  can_submit_encounter_questionnaire: "Encounter / Forms",
  can_list_booking: "Appointments / Read",
  can_write_booking: "Appointments / Edit",
  can_reschedule_booking: "Appointments / Reschedule",
  can_access_practitioner_workspace: "Appointments / Practitioner workspace",
  can_access_nursing_actions: "Appointments / Nursing actions",
  can_access_care_team_encounters: "Encounter / Care-team access",
  can_read_invoice: "Billing / View invoices",
  can_read_charge_item: "Billing / View charge items",
  can_write_invoice: "Billing / Create invoices",
  can_read_payment_reconciliation: "Billing / View payments",
  can_write_payment_reconciliation: "Billing / Record payments",
  can_access_consultation_billing: "Billing / Consultation scope",
  can_access_diagnostic_billing: "Billing / Diagnostic scope",
  can_access_pharmacy_billing: "Billing / Pharmacy scope",
  can_access_insurance_billing: "Billing / Insurance scope",
  can_read_account: "Billing / View accounts",
  can_update_account: "Billing / Update accounts",
  can_create_account: "Billing / Create accounts",
  can_view_facility_general_settings: "Settings / General",
  can_update_facility: "Settings / General edit",
  can_create_facility_organization: "Settings / Departments create",
  can_manage_facility_organization: "Settings / Departments manage",
  can_manage_facility_organization_users: "Settings / Department users manage",
  can_write_facility_locations: "Settings / Locations manage",
  can_manage_devices: "Settings / Devices manage",
  can_write_specimen_definition: "Settings / Specimen definitions manage",
  can_write_observation_definition: "Settings / Observation definitions manage",
  can_write_activity_definition: "Settings / Activity definitions manage",
  can_set_charge_item_definition: "Settings / Billing price setup",
  can_write_charge_item_definition: "Settings / Charge item definitions manage",
  can_write_healthcare_service: "Settings / Healthcare services manage",
  can_write_product_knowledge: "Settings / Product knowledge manage",
  can_write_product: "Settings / Product manage",
  can_write_token_category: "Settings / Token category manage",
  can_write_patient_identifier_config:
    "Settings / Patient identifier config manage",
  can_write_tag_config: "Settings / Tag config manage",
  can_write_template: "Settings / Templates manage",
  can_manage_template: "Settings / Templates admin",
  can_view_facility_organization: "Facility / Department read",
  can_list_facility_organization_users: "Facility / Department users",
  can_list_patients: "Patient / Read",
  can_read_patient_identifier_config: "Patient / Identifier read",
  can_create_patient: "Patient / Create",
  can_write_patient: "Patient / Edit",
  can_view_clinical_data: "Patient / Clinical read",
  can_submit_patient_questionnaire: "Patient / Forms",
  can_view_questionnaire_responses: "Patient / Form responses",
};

const CLINICAL_ROLE_PERMISSIONS = [
  "can_apply_tag_config",
  "can_access_care_team_encounters",
  "can_create_charge_item",
  "can_create_encounter",
  "can_create_patient",
  "can_generate_report_from_template",
  "can_list_booking",
  "can_list_devices",
  "can_list_encounter",
  "can_list_facility_locations",
  "can_list_facility_organization_users",
  "can_list_organization_users",
  "can_list_patients",
  "can_list_schedule",
  "can_list_token",
  "can_list_token_category",
  "can_list_user",
  "can_manage_device_associations_to_encounters",
  "can_read_account",
  "can_read_activity_definition",
  "can_read_charge_item",
  "can_read_charge_item_definition",
  "can_read_diagnostic_report",
  "can_read_encounter",
  "can_read_encounter_clinical_data",
  "can_read_facility",
  "can_read_healthcare_service",
  "can_read_inventory_item",
  "can_read_invoice",
  "can_read_observation_definition",
  "can_read_patient_identifier_config",
  "can_read_payment_reconciliation",
  "can_read_product",
  "can_read_product_knowledge",
  "can_read_questionnaire",
  "can_read_resource_category",
  "can_read_service_request",
  "can_read_specimen",
  "can_read_specimen_definition",
  "can_read_supply_delivery",
  "can_read_supply_request",
  "can_read_tag_config",
  "can_read_template",
  "can_reschedule_booking",
  "can_submit_encounter_questionnaire",
  "can_submit_patient_questionnaire",
  "can_submit_questionnaire",
  "can_view_clinical_data",
  "can_view_facility_organization",
  "can_view_organization",
  "can_view_questionnaire_responses",
  "can_write_booking",
  "can_write_diagnostic_report",
  "can_write_encounter",
  "can_write_encounter_clinical_data",
  "can_write_invoice",
  "can_write_patient",
  "can_write_payment_reconciliation",
  "can_write_schedule",
  "can_write_service_request",
  "can_write_specimen",
  "can_write_supply_request",
  "can_write_token",
  "read_medication_dispense",
  "write_medication_dispense",
];

const DOCTOR_ROLE_PERMISSIONS = [
  ...CLINICAL_ROLE_PERMISSIONS,
  "can_access_practitioner_workspace",
];

const NURSE_ROLE_PERMISSIONS = [
  ...DOCTOR_ROLE_PERMISSIONS,
  "can_access_nursing_actions",
];

const ROLE_PERMISSION_PRESETS = [
  {
    key: "frontdesk",
    label: "Frontdesk Staff",
    description:
      "Registration, patient search, appointment booking and consultation billing",
    permissions: [
      "can_read_facility",
      "can_list_patients",
      "can_read_patient_identifier_config",
      "can_read_tag_config",
      "can_create_patient",
      "can_write_patient",
      "can_list_booking",
      "can_write_booking",
      "can_reschedule_booking",
      "can_list_schedule",
      "can_create_encounter",
      "can_list_encounter",
      "can_read_encounter",
      "can_list_facility_locations",
      "can_create_account",
      "can_read_account",
      "can_update_account",
      "can_read_charge_item",
      "can_read_invoice",
      "can_write_invoice",
      "can_read_payment_reconciliation",
      "can_write_payment_reconciliation",
      "can_access_consultation_billing",
    ],
  },
  {
    key: "doctor",
    label: "Doctor",
    description: "Encounter workspace, clinical write, orders and reports",
    permissions: DOCTOR_ROLE_PERMISSIONS,
  },
  {
    key: "nurse",
    label: "Nurse",
    description: "Patient follow-up and nursing clinical documentation",
    permissions: NURSE_ROLE_PERMISSIONS,
  },
  {
    key: "pharmacy",
    label: "Pharmacy Staff",
    description: "Dispense workflow, stock visibility and prescription support",
    permissions: [
      "can_create_account",
      "can_list_encounter",
      "can_list_facility_locations",
      "can_read_facility",
      "can_list_patients",
      "can_view_clinical_data",
      "can_read_account",
      "can_read_charge_item",
      "can_read_charge_item_definition",
      "can_read_encounter",
      "can_read_encounter_clinical_data",
      "can_read_healthcare_service",
      "can_read_invoice",
      "can_read_patient_identifier_config",
      "read_medication_dispense",
      "write_medication_dispense",
      "can_read_inventory_item",
      "can_read_product",
      "can_read_product_knowledge",
      "can_read_resource_category",
      "can_read_supply_delivery",
      "can_read_supply_request",
      "can_read_tag_config",
      "can_update_account",
      "can_write_invoice",
      "is_pharmacist",
    ],
  },
  {
    key: "inventory",
    label: "Inventory Officer",
    description: "Product, stock and inward supply handling",
    permissions: [
      "can_read_facility",
      "can_list_facility_locations",
      "can_read_healthcare_service",
      "can_read_inventory_item",
      "can_read_product",
      "can_write_product",
      "can_read_product_knowledge",
      "can_write_product_knowledge",
      "can_read_resource_category",
      "can_read_supply_request",
      "can_write_supply_request",
      "can_read_supply_delivery",
      "can_write_supply_delivery",
    ],
  },
  {
    key: "lab-staff",
    label: "Lab Staff",
    description:
      "Specimen collection, lab service requests and diagnostic reports",
    permissions: [
      "can_create_account",
      "can_create_charge_item",
      "can_list_encounter",
      "can_list_facility_locations",
      "can_read_facility",
      "can_list_patients",
      "can_view_clinical_data",
      "can_read_account",
      "can_read_activity_definition",
      "can_read_charge_item",
      "can_read_charge_item_definition",
      "can_read_diagnostic_report",
      "can_read_encounter",
      "can_read_encounter_clinical_data",
      "can_read_healthcare_service",
      "can_read_invoice",
      "can_read_observation_definition",
      "can_read_patient_identifier_config",
      "can_read_resource_category",
      "can_read_service_request",
      "can_read_specimen",
      "can_read_specimen_definition",
      "can_read_tag_config",
      "can_update_account",
      "can_write_diagnostic_report",
      "can_write_invoice",
      "can_write_service_request",
      "can_write_specimen",
    ],
  },
  {
    key: "lab-head",
    label: "Lab Head",
    description:
      "Shared laboratory workflow for known lab services and team oversight",
    permissions: [
      "can_create_account",
      "can_create_charge_item",
      "can_list_encounter",
      "can_list_facility_locations",
      "can_read_facility",
      "can_list_patients",
      "can_view_clinical_data",
      "can_read_account",
      "can_read_activity_definition",
      "can_read_charge_item",
      "can_read_charge_item_definition",
      "can_read_diagnostic_report",
      "can_read_encounter",
      "can_read_encounter_clinical_data",
      "can_read_healthcare_service",
      "can_read_invoice",
      "can_read_observation_definition",
      "can_read_patient_identifier_config",
      "can_read_payment_reconciliation",
      "can_read_resource_category",
      "can_read_service_request",
      "can_read_specimen",
      "can_read_specimen_definition",
      "can_read_tag_config",
      "can_update_account",
      "can_write_diagnostic_report",
      "can_write_invoice",
      "can_write_payment_reconciliation",
      "can_write_service_request",
      "can_write_specimen",
    ],
  },
  {
    key: "billing",
    label: "Billing Staff",
    description:
      "Consultation, diagnostic, pharmacy and insurance billing with invoice and payment operations",
    permissions: [
      "can_read_facility",
      "can_list_patients",
      "can_write_patient",
      "can_read_patient_identifier_config",
      "can_read_tag_config",
      "can_list_facility_locations",
      "can_read_product",
      "can_create_account",
      "can_read_account",
      "can_update_account",
      "can_create_charge_item",
      "can_read_charge_item",
      "can_update_charge_item",
      "can_read_charge_item_definition",
      "can_write_charge_item_definition",
      "can_set_charge_item_definition",
      "can_read_invoice",
      "can_write_invoice",
      "can_destroy_invoice",
      "can_manage_locked_invoice",
      "can_read_payment_reconciliation",
      "can_write_payment_reconciliation",
      "can_destroy_payment_reconciliation",
      "can_access_consultation_billing",
      "can_access_diagnostic_billing",
      "can_access_pharmacy_billing",
      "can_access_insurance_billing",
      "can_list_facility_locations",
    ],
  },
  {
    key: "facility-admin",
    label: "Facility Admin",
    description: "Users, departments, schedules, settings and facility setup",
    permissions: [
      "can_read_facility",
      "can_update_facility",
      "can_create_user",
      "can_create_service_account",
      "can_list_user",
      "can_list_schedule",
      "can_write_schedule",
      "can_list_booking",
      "can_write_booking",
      "can_reschedule_booking",
      "can_list_token",
      "can_write_token",
      "can_list_token_category",
      "can_write_token_category",
      "can_list_devices",
      "can_manage_devices",
      "can_list_facility_locations",
      "can_write_facility_locations",
      "can_create_facility_location_organizations",
      "can_list_facility_location_organizations",
      "can_read_healthcare_service",
      "can_write_healthcare_service",
      "can_read_charge_item_definition",
      "can_write_charge_item_definition",
      "can_set_charge_item_definition",
      "can_read_activity_definition",
      "can_write_activity_definition",
      "can_read_observation_definition",
      "can_write_observation_definition",
      "can_read_patient_identifier_config",
      "can_write_patient_identifier_config",
      "can_read_tag_config",
      "can_write_tag_config",
      "can_read_template",
      "can_write_template",
      "can_preview_template",
      "can_generate_report_from_template",
      "can_view_facility_organization",
      "can_create_facility_organization",
      "can_manage_facility_organization",
      "can_list_facility_organization_users",
      "can_manage_facility_organization_users",
      "can_view_organization",
      "can_list_organization_users",
    ],
  },
];

function expandPermissionSelection(permissionSlugs: string[]) {
  const expanded = new Set(permissionSlugs);
  let changed = true;

  while (changed) {
    changed = false;
    Array.from(expanded).forEach((slug) => {
      PERMISSION_DEPENDENCIES[slug]?.forEach((dependency) => {
        if (!expanded.has(dependency)) {
          expanded.add(dependency);
          changed = true;
        }
      });
    });
  }

  return Array.from(expanded);
}

function getNewDependencySelections(
  permissionSlugs: string[],
  currentPermissionSlugs: string[],
) {
  const current = new Set(currentPermissionSlugs);
  return expandPermissionSelection(permissionSlugs).filter(
    (slug) => !permissionSlugs.includes(slug) && !current.has(slug),
  );
}

function prunePermissionSelection(permissionSlugs: string[]) {
  const pruned = new Set(permissionSlugs);
  let changed = true;

  while (changed) {
    changed = false;
    Array.from(pruned).forEach((slug) => {
      const dependencies = PERMISSION_DEPENDENCIES[slug] ?? [];
      const missingDependency = dependencies.some(
        (dependency) => !pruned.has(dependency),
      );

      if (missingDependency) {
        pruned.delete(slug);
        changed = true;
      }
    });
  }

  return Array.from(pruned);
}

function getPermissionGroup(permission: Permission) {
  return (
    PERMISSION_GROUPS.find((group) => group.matches(permission.slug)) ?? {
      key: "other",
      label: "Other",
      matches: () => true,
    }
  );
}

export default function RoleForm({ role, onSuccess }: RoleFormProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchPermission, setSearchPermission] = useState("");
  const [permissionViewMode, setPermissionViewMode] =
    useState<PermissionViewMode>("all");
  const [expandedDependencySlug, setExpandedDependencySlug] = useState<
    string | null
  >(null);
  const [lastAutoSelectedPermissionSlugs, setLastAutoSelectedPermissionSlugs] =
    useState<string[]>([]);

  const [formData, setFormData] = React.useState({
    name: role?.name || "",
    description: role?.description || "",
    permissions: role?.permissions.map((p: Permission) => p.slug) || [],
  });

  const getQueryParams = (pageParam: number, name: string) => ({
    limit: String(PAGE_LIMIT),
    offset: String(pageParam),
    name: name,
  });

  const { data: permissionsList, isFetching } = useInfiniteQuery({
    queryKey: ["permissions", searchPermission],
    queryFn: async ({ pageParam = 0, signal }) => {
      const response = await query.debounced(permissionApi.listPermissions, {
        queryParams: getQueryParams(pageParam, searchPermission),
      })({ signal });
      return response;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const currentOffset = allPages.length * PAGE_LIMIT;
      return currentOffset < lastPage.count ? currentOffset : null;
    },
    select: (data) => data?.pages.flatMap((p) => p.results) || [],
  });

  const permissions = useMemo(() => permissionsList || [], [permissionsList]);
  const permissionNameMap = useMemo(
    () =>
      new Map(
        permissions.map((permission) => [permission.slug, permission.name]),
      ),
    [permissions],
  );

  const visiblePermissions = useMemo(() => {
    const selected = permissions.filter((permission) =>
      formData.permissions.includes(permission.slug),
    );
    const unselected = permissions.filter(
      (permission) => !formData.permissions.includes(permission.slug),
    );

    if (permissionViewMode === "selected") {
      return selected;
    }
    if (permissionViewMode === "unselected") {
      return unselected;
    }
    return [...selected, ...unselected];
  }, [formData.permissions, permissionViewMode, permissions]);

  const groupedPermissions = useMemo(() => {
    const groups = new Map<
      string,
      { label: string; permissions: Permission[] }
    >();
    visiblePermissions.forEach((permission) => {
      const group = getPermissionGroup(permission);
      const existing = groups.get(group.key) ?? {
        label: group.label,
        permissions: [],
      };
      existing.permissions.push(permission);
      groups.set(group.key, existing);
    });
    return Array.from(groups.entries()).map(([key, group]) => ({
      key,
      ...group,
    }));
  }, [visiblePermissions]);

  const permissionDependencyLabels = useMemo(() => {
    return Object.fromEntries(
      Object.entries(PERMISSION_DEPENDENCIES).map(([slug, dependencies]) => [
        slug,
        dependencies.map(
          (dependency) =>
            permissionNameMap.get(dependency) ??
            PERMISSION_PARENT_LABELS[dependency] ??
            dependency,
        ),
      ]),
    );
  }, [permissionNameMap]);

  const createRoleMutation = useMutation({
    mutationFn: mutate(roleApi.createRole),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      onSuccess();
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: mutate(roleApi.updateRole, {
      pathParams: { external_id: role?.id || "" },
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      onSuccess();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      name: formData.name,
      description: formData.description,
      permissions: formData.permissions,
    };

    if (role?.id) {
      updateRoleMutation.mutate(payload);
    } else {
      createRoleMutation.mutate(payload);
    }
  };

  const handlePermissionToggle = (permissionSlug: string) => {
    const wasSelected = formData.permissions.includes(permissionSlug);
    const autoSelectedSlugs = wasSelected
      ? []
      : getNewDependencySelections([permissionSlug], formData.permissions);

    setFormData((prev) => ({
      ...prev,
      permissions: wasSelected
        ? prunePermissionSelection(
            prev.permissions.filter((p: string) => p !== permissionSlug),
          )
        : expandPermissionSelection([...prev.permissions, permissionSlug]),
    }));

    if (autoSelectedSlugs.length > 0) {
      setExpandedDependencySlug(permissionSlug);
      setLastAutoSelectedPermissionSlugs(autoSelectedSlugs);
    } else if (wasSelected && expandedDependencySlug === permissionSlug) {
      setExpandedDependencySlug(null);
      setLastAutoSelectedPermissionSlugs([]);
    }
  };

  const handlePermissionGroupToggle = (permissionSlugs: string[]) => {
    setFormData((prev) => {
      const currentPermissions = new Set(prev.permissions);
      const allSelected = permissionSlugs.every((slug) =>
        currentPermissions.has(slug),
      );

      if (allSelected) {
        permissionSlugs.forEach((slug) => currentPermissions.delete(slug));
        return {
          ...prev,
          permissions: prunePermissionSelection(Array.from(currentPermissions)),
        };
      } else {
        expandPermissionSelection(permissionSlugs).forEach((slug) =>
          currentPermissions.add(slug),
        );
      }

      return {
        ...prev,
        permissions: Array.from(currentPermissions),
      };
    });
  };

  const handlePresetApply = (permissionSlugs: string[]) => {
    setFormData((prev) => ({
      ...prev,
      permissions: Array.from(
        new Set(
          expandPermissionSelection([...prev.permissions, ...permissionSlugs]),
        ),
      ),
    }));
  };

  const isLoading =
    createRoleMutation.isPending || updateRoleMutation.isPending;

  return (
    <>
      <form onSubmit={handleSubmit} className="flex flex-col space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t("name")}</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder={t("enter_role_name")}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">{t("description")}</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder={t("enter_role_description")}
              rows={3}
            />
          </div>
        </div>

        <Card className="flex flex-col">
          <CardHeader className="flex flex-col">
            <div className="flex items-center justify-between">
              <CardTitle>{t("permissions")}</CardTitle>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      permissions: permissions.map((p) => p.slug),
                    }))
                  }
                >
                  {t("select_all")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      permissions: [],
                    }))
                  }
                >
                  {t("clear")}
                </Button>
              </div>
            </div>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 size-4" />
              <Input
                placeholder={t("search_permissions")}
                value={searchPermission}
                onChange={(e) => {
                  setSearchPermission(e.target.value);
                }}
                className="w-full pl-8"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                {
                  key: "all" as const,
                  label: t("all_permissions"),
                  count: permissions.length,
                },
                {
                  key: "selected" as const,
                  label: t("selected_permissions"),
                  count: formData.permissions.length,
                },
                {
                  key: "unselected" as const,
                  label: t("unselected_permissions"),
                  count: Math.max(
                    permissions.length - formData.permissions.length,
                    0,
                  ),
                },
              ].map((mode) => (
                <Button
                  key={mode.key}
                  type="button"
                  variant={
                    permissionViewMode === mode.key ? "primary" : "outline"
                  }
                  size="sm"
                  onClick={() => setPermissionViewMode(mode.key)}
                >
                  {mode.label}
                  <Badge
                    variant={
                      permissionViewMode === mode.key ? "secondary" : "outline"
                    }
                    className="ml-2 rounded-full px-1.5 py-0 text-[10px]"
                  >
                    {mode.count}
                  </Badge>
                </Button>
              ))}
            </div>
            <div className="space-y-2 rounded-md border bg-gray-50 p-3">
              <div className="text-sm font-medium text-gray-700">
                {t("suggested_permission_sets")}
              </div>
              <div className="flex flex-wrap gap-2">
                {ROLE_PERMISSION_PRESETS.map((preset) => {
                  const expandedPreset = expandPermissionSelection(
                    preset.permissions,
                  );
                  const selectedCount = expandedPreset.filter((slug) =>
                    formData.permissions.includes(slug),
                  ).length;
                  const isComplete = selectedCount === expandedPreset.length;

                  return (
                    <Button
                      key={preset.key}
                      type="button"
                      variant={isComplete ? "primary" : "outline"}
                      size="sm"
                      className="h-auto rounded-full px-3 py-1.5"
                      title={preset.description}
                      onClick={() => handlePresetApply(preset.permissions)}
                    >
                      {preset.label}
                      <Badge
                        variant={isComplete ? "secondary" : "outline"}
                        className="ml-2 rounded-full px-1.5 py-0 text-[10px]"
                      >
                        {selectedCount}/{expandedPreset.length}
                      </Badge>
                    </Button>
                  );
                })}
              </div>
              <div className="text-xs text-gray-500">
                {t("permission_suggestions_description")}
              </div>
            </div>
          </CardHeader>
          <CardContent className="h-[45vh] overflow-y-auto">
            <div className="space-y-3 h-full">
              {groupedPermissions.map((group) => {
                const permissionSlugs = group.permissions.map(
                  (permission) => permission.slug,
                );
                const checkedCount = permissionSlugs.filter((slug) =>
                  formData.permissions.includes(slug),
                ).length;
                const allSelected =
                  permissionSlugs.length > 0 &&
                  checkedCount === permissionSlugs.length;

                return (
                  <div key={group.key} className="rounded-md border p-3">
                    <div className="mb-3 flex items-center gap-2 border-b pb-2">
                      <Checkbox
                        id={`permission-group-${group.key}`}
                        checked={allSelected}
                        onCheckedChange={() =>
                          handlePermissionGroupToggle(permissionSlugs)
                        }
                      />
                      <Label
                        htmlFor={`permission-group-${group.key}`}
                        className="cursor-pointer font-semibold"
                      >
                        {group.label}
                        <span className="ml-2 text-xs font-normal text-gray-500">
                          {checkedCount}/{permissionSlugs.length}
                        </span>
                      </Label>
                    </div>
                    <div className="grid gap-3 grid-cols-1 xl:grid-cols-2">
                      {group.permissions.map((permission) => {
                        const dependencies =
                          permissionDependencyLabels[permission.slug] ?? [];
                        const hierarchyLabel =
                          PERMISSION_PARENT_LABELS[permission.slug];
                        const isDependencyPanelOpen =
                          expandedDependencySlug === permission.slug;
                        const autoSelectedDependencies =
                          isDependencyPanelOpen &&
                          lastAutoSelectedPermissionSlugs.length > 0
                            ? lastAutoSelectedPermissionSlugs
                            : (PERMISSION_DEPENDENCIES[permission.slug] ?? []);

                        return (
                          <div
                            key={permission.slug}
                            className="rounded-md border border-gray-100 bg-gray-50/60 p-3"
                          >
                            <div className="flex items-start gap-3">
                              <Checkbox
                                id={permission.slug}
                                checked={formData.permissions.includes(
                                  permission.slug,
                                )}
                                onCheckedChange={() =>
                                  handlePermissionToggle(permission.slug)
                                }
                              />
                              <Label
                                htmlFor={permission.slug}
                                className="flex-1 cursor-pointer"
                              >
                                <div className="space-y-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-medium">
                                      {permission.name}
                                    </span>
                                    {hierarchyLabel && (
                                      <span className="rounded bg-white px-2 py-0.5 text-xs text-gray-600 ring-1 ring-gray-200">
                                        {hierarchyLabel}
                                      </span>
                                    )}
                                    {dependencies.length > 0 && (
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="xs"
                                        className="h-6 px-2 text-xs"
                                        onClick={(event) => {
                                          event.preventDefault();
                                          setExpandedDependencySlug(
                                            isDependencyPanelOpen
                                              ? null
                                              : permission.slug,
                                          );
                                          setLastAutoSelectedPermissionSlugs(
                                            [],
                                          );
                                        }}
                                      >
                                        {t("includes_permissions")}{" "}
                                        {dependencies.length}
                                      </Button>
                                    )}
                                  </div>
                                  <div className="font-mono text-xs text-gray-500 break-all">
                                    {permission.slug}
                                  </div>
                                  {permission.description && (
                                    <div className="text-sm text-gray-500">
                                      {permission.description}
                                    </div>
                                  )}
                                </div>
                              </Label>
                            </div>
                            {isDependencyPanelOpen &&
                              autoSelectedDependencies.length > 0 && (
                                <div className="mt-3 rounded-md border bg-white p-3">
                                  <div className="mb-2 text-xs font-medium text-gray-600">
                                    {lastAutoSelectedPermissionSlugs.length > 0
                                      ? t("auto_selected_permissions")
                                      : t("includes_permissions")}
                                  </div>
                                  <div className="grid gap-2 grid-cols-1 md:grid-cols-2">
                                    {autoSelectedDependencies.map((slug) => (
                                      <div
                                        key={slug}
                                        className="rounded border border-gray-100 bg-gray-50 px-2 py-1.5"
                                      >
                                        <div className="text-xs font-medium text-gray-700">
                                          {permissionNameMap.get(slug) ??
                                            PERMISSION_PARENT_LABELS[slug] ??
                                            slug}
                                        </div>
                                        <div className="font-mono text-[11px] text-gray-500">
                                          {slug}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {isFetching ? (
                <div className="text-center text-sm">{t("loading")}</div>
              ) : (
                visiblePermissions.length == 0 && (
                  <div className="text-center text-sm">
                    {t("no_matching_permissions")}
                  </div>
                )
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end space-x-2">
          <Button
            type="button"
            variant="outline"
            onClick={onSuccess}
            disabled={isLoading}
          >
            {t("cancel")}
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading
              ? t("saving")
              : role?.id
                ? t("update_role")
                : t("create_role")}
          </Button>
        </div>
      </form>
    </>
  );
}
